import { PayloadAction, SerializedError, createSelector, createSlice } from "@reduxjs/toolkit";
import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import type { RootState } from "_shared/_stores/root-reducer";
import Guid from "_shared/guids/guid";
import UserCoworkerQuery from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import { userCoworkerApi } from "users/information/coworker-information/_api/user-coworker-api";
import { UserCoworkerJSON } from "users/information/coworker-information/_schemas/user-coworker-schema";
import UserCoworker from "users/information/coworker-information/user-coworker";

export type UserCoworkerStoreState = {
  byQuery: {
    entries: Record<string, Paginated<UserCoworkerJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  byId: {
    entries: Record<string, UserCoworkerJSON>;
  };
  byIndividualId: {
    entries: Record<string, UserCoworkerJSON>;
  };
  byUserId: {
    entries: Record<string, UserCoworkerJSON>;
  };
};

const initialState: UserCoworkerStoreState = {
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  byId: {
    entries: {},
  },
  byIndividualId: {
    entries: {},
  },
  byUserId: {
    entries: {},
  },
};

function upsertCoworkerJSON(state: UserCoworkerStoreState, coworkerJSON: UserCoworkerJSON): void {
  if (coworkerJSON.id) {
    state.byId.entries[coworkerJSON.id] = coworkerJSON;
  }
  state.byIndividualId.entries[coworkerJSON.individualId] = coworkerJSON;
  state.byUserId.entries[coworkerJSON.userId] = coworkerJSON;
}

function mergeCoworkerPages(
  current: Paginated<UserCoworkerJSON> | undefined,
  next: Paginated<UserCoworkerJSON>,
): Paginated<UserCoworkerJSON> {
  if (!current) return next;

  const merged = [...current.data];
  const indexesByUserId = new Map(merged.map((coworker, index) => [coworker.userId, index]));
  for (const coworker of next.data) {
    const existingIndex = indexesByUserId.get(coworker.userId);
    if (existingIndex === undefined) {
      indexesByUserId.set(coworker.userId, merged.length);
      merged.push(coworker);
    } else {
      merged[existingIndex] = coworker;
    }
  }

  return {
    data: merged,
    headers: next.headers,
  };
}

export const userCoworkersSlice = createSlice({
  name: "userCoworkers",
  initialState,
  reducers: {
    addUserCoworkerJSON: (state, action: PayloadAction<UserCoworkerJSON>) => {
      upsertCoworkerJSON(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(userCoworkerApi.endpoints.getUserCoworkers.matchPending, (state, action) => {
      const cacheKey = action.meta.arg.originalArgs.query.cacheKey;
      state.byQuery.loading[cacheKey] = true;
      state.byQuery.error[cacheKey] = null;
    });

    builder.addMatcher(userCoworkerApi.endpoints.getUserCoworkers.matchFulfilled, (state, action) => {
      const { query, append } = action.meta.arg.originalArgs;
      const cacheKey = query.cacheKey;
      for (const coworkerJSON of action.payload.data) {
        upsertCoworkerJSON(state, coworkerJSON);
      }
      state.byQuery.entries[cacheKey] = append
        ? mergeCoworkerPages(state.byQuery.entries[cacheKey], action.payload)
        : action.payload;
      state.byQuery.loading[cacheKey] = false;
    });

    builder.addMatcher(userCoworkerApi.endpoints.getUserCoworkers.matchRejected, (state, action) => {
      const cacheKey = action.meta.arg.originalArgs.query.cacheKey;
      state.byQuery.loading[cacheKey] = false;
      state.byQuery.error[cacheKey] = action.error;
    });
  },
});

export const { addUserCoworkerJSON } = userCoworkersSlice.actions;

const selectUserCoworkerQueryEntries = (state: RootState) => state.userCoworkers.byQuery.entries;
const selectUserCoworkersByQueryKey = createSelector(
  [selectUserCoworkerQueryEntries, (_: RootState, cacheKey: string) => cacheKey],
  (entries, cacheKey) => {
    const page = entries[cacheKey];
    if (!page) return PaginatedResponse.empty<UserCoworker>();
    return PaginatedResponse.from(UserCoworker.fromJSONArray(page.data), page.headers);
  },
);

export const getUserCoworkersByQuery = (query: UserCoworkerQuery) => (state: RootState) =>
  selectUserCoworkersByQueryKey(state, query.cacheKey);

export const getIsLoadingUserCoworkersByQuery = (query: UserCoworkerQuery) => (state: RootState) =>
  state.userCoworkers.byQuery.loading[query.cacheKey] ?? false;

export const getErrorLoadingUserCoworkersByQuery = (query: UserCoworkerQuery) => (state: RootState) =>
  state.userCoworkers.byQuery.error[query.cacheKey] ?? null;

const selectUserCoworkerByUserId = createSelector(
  [(state: RootState) => state.userCoworkers.byUserId.entries, (_: RootState, userId?: Guid) => userId?.value],
  (entries, userId) => {
    if (!userId) return;
    const coworkerJSON = entries[userId];
    return coworkerJSON ? UserCoworker.fromJSON(coworkerJSON) : undefined;
  },
);
export const getUserCoworkerByUserId = (userId?: Guid) => (state: RootState) =>
  selectUserCoworkerByUserId(state, userId);

const selectUserCoworkersByUserIds = createSelector(
  [(state: RootState) => state.userCoworkers.byUserId.entries, (_: RootState, userIds: Guid[] | Set<string>) => userIds],
  (entries, userIds) => {
    const idsArray = Array.isArray(userIds) ? userIds.map((id) => id.value) : Array.from(userIds);
    return idsArray
      .map((userId) => {
        const coworkerJSON = entries[userId];
        return coworkerJSON ? UserCoworker.fromJSON(coworkerJSON) : undefined;
      })
      .filter((coworker): coworker is UserCoworker => !!coworker);
  },
);
export const getUserCoworkersByUserIds = (userIds: Guid[] | Set<string>) => (state: RootState) =>
  selectUserCoworkersByUserIds(state, userIds);

const selectUserCoworkerByIndividualId = createSelector(
  [
    (state: RootState) => state.userCoworkers.byIndividualId.entries,
    (_: RootState, individualId?: Guid) => individualId?.value,
  ],
  (entries, individualId) => {
    if (!individualId) return;
    const coworkerJSON = entries[individualId];
    return coworkerJSON ? UserCoworker.fromJSON(coworkerJSON) : undefined;
  },
);
export const getUserCoworkerByIndividualId = (individualId?: Guid) => (state: RootState) =>
  selectUserCoworkerByIndividualId(state, individualId);
